import { win } from "./lib/pd-window.js";
import {
  trialFormAttemptedSignup,
  trialFormNextButtonClicked,
  trialFormElementUserInputDetected,
  trialSingleLineFormViewed,
  trialFormDependencyActivation,
  trackCloudflareEnrichment,
  trialFormBackButtonClicked,
  trialFormPasswordVisibilityToggled,
} from "./segment-tracks.js";
import { cookie, createPDCookie, getPDCookie } from "./lib/cookies";
import { isPdEmployee } from "./lib/pd-helpers";
import {
  hideIndividualErrors,
  showSpecificError,
  checkEmailValidity,
  getEmailValidity,
  mkwid,
  gclid,
} from "./lib/sign-up";
import { selectInput, selectSelect } from "./lib/form-helpers.js";
import { uuidNoDashes as createRequestId } from "./lib/uuid.js";
import { identifyFreeTrialShortForm } from "./segment/segment-calls.js";
import { countryLookupTable, getCloudflareData } from "./lib/cf-helpers.js";
import { createSignupResponseError } from "./lib/signup-error-utils.js";
import { loadTurnstileWithRetry } from "./lib/turnstile-loader.js";
import {
  createSignupValidationError,
  getSignupValidationErrorFields,
  isActionableSignupValidationError,
} from "./lib/signup-error-utils.js";

const TRIAL_FORM_DATA_LS_KEY = "pd_trial_form";
const TRIAL_FORM_DATA_MAX_AGE = 30 * 60 * 1000; // 30 minutes (session duration)

const employeeSearchCriteria = ["Email", "First-name", "Last-name"];

/** @type {import("./lib/sign-up.js").TurnstileValidityState} */
const turnstileValidityState = {
  turnstileResponseError: false,
  turnstileValueMissing: false,
};

function displayTurnstileValidity() {
  const turnstileErrorsElm = selectById("turnstile-errors");
  if (!(
    turnstileValidityState.turnstileResponseError ||
    turnstileValidityState.turnstileValueMissing
  )) {
    // No errors
    turnstileErrorsElm.classList.add("hidden");
  } else {
    turnstileErrorsElm.classList.remove("hidden");
    hideIndividualErrors(turnstileErrorsElm);
    showSpecificError(turnstileErrorsElm, turnstileValidityState);
  }
}

export function initialize() {
  const formElm = selectForm();
  formElm.noValidate = true;
  formElm.addEventListener("submit", onSubmit);
  const step = initFirstStep();
  step.classList.remove("hidden");
  onFormFirstView(() => {
    setFocus(step);
    trialSingleLineFormViewed();
  });
  setupInputListeners();
  setupBackButtonListeners();
  setupPasswordVisibilityButton();
  loadTurnstile(getCurrentStep());
  initCloudflareEnrichment();
  // trapDoorSSOExperiment();
}

/** @arg {Step} step */
function loadTurnstile(step) {
  displayTurnstileValidity();

  loadTurnstileWithRetry({
    win,
    document,
    renderTurnstile() {
      const conf = {
        // For debugging
        // Sitekey                  Description                     Visibility
        // 1x00000000000000000000AA Always passes                   visible
        // 2x00000000000000000000AB Always blocks                   visible
        // 1x00000000000000000000BB Always passes                   invisible
        // 2x00000000000000000000BB Always blocks                   invisible
        // 3x00000000000000000000FF Forces an interactive challenge visible
        // sitekey: "2x00000000000000000000AB",
        theme: "light",
        "refresh-expired": "auto",
        /** @arg {string} token */
        callback(token) {
          const hasToken = token != null;
          toggleSubmitBtn(hasToken);
          turnstileValidityState.turnstileValueMissing = !hasToken;
          displayTurnstileValidity();
        },
        "before-interactive-callback"() {
          toggleSubmitBtn(false);
          turnstileValidityState.turnstileValueMissing = true;
          displayTurnstileValidity();
        },
        "error-callback"() {
          toggleSubmitBtn(false);
          turnstileValidityState.turnstileResponseError = true;
          displayTurnstileValidity();
        },
        "timeout-callback"() {
          toggleSubmitBtn(false);
        },
        "expired-callback"() {
          toggleSubmitBtn(false);
        },
      };

      if (win.turnstile == null) {
        throw new Error("Turnstile loaded without window.turnstile");
      }
      win.turnstile.render("#cf-turnstile", conf);
    },
    onFinalFailure(failure) {
      swapSteps(step, "failure");
      trackTurnstileNotloaded(step);
      win.Bugsnag?.notify(new Error("Turnstile failed to load"), (event) => {
        event.severity = "warning";
        event.addMetadata("turnstile", {
          scriptSrc: failure.scriptSrc,
          step,
          attempt: failure.attempt,
          maxAttempts: failure.maxAttempts,
          failureType: failure.failureType,
          hasTurnstile: failure.hasTurnstile,
        });
      });
    },
  });
}

function resetTurnstile() {
  if (win.turnstile != null && typeof win.turnstile["reset"] === "function") {
    win.turnstile["reset"]("#cf-turnstile");
  }
}

function initFirstStep() {
  const sessEmail = sessionStorage.getItem("email");
  const trialFormData = getTrialFormDataFromLocalStorage();

  // Check for email in session storage
  if (sessEmail !== null && setEmailValue(sessEmail)) {
    const currentStep = getCurrentStep(); // Force start on step 2
    nextStep[currentStep]();
  }
  // Check for trial form data in localStorage
  else if (trialFormData) {
    restoreAbandonedForm(trialFormData);
  }
  return selectStep(getCurrentStep());
}

/** @typedef {(Record<Step, Record<string, string>> & { timestamp: number }) | null} TrialFormData */

/**
 * Get trial form data from local storage
 * @returns {TrialFormData}
 */
export function getTrialFormDataFromLocalStorage() {
  try {
    const dataStr = localStorage.getItem(TRIAL_FORM_DATA_LS_KEY);
    if (!dataStr) return null;

    const data = JSON.parse(dataStr);

    if (isTrialFormDataExpired(data)) {
      removeTrialFormDataFromLocalStorage();
      return null;
    }

    return data;
  } catch (err) {
    return null;
  }
}

/**
 * Save step data to local storage
 * @param {Step} step
 * @param {Record<string, string>} data
 */
function saveStepToLocalStorage(step, data) {
  try {
    const existingData = JSON.parse(
      localStorage.getItem(TRIAL_FORM_DATA_LS_KEY) || "{}",
    );
    existingData[step] = data;
    existingData["timestamp"] = Date.now();
    localStorage.setItem(TRIAL_FORM_DATA_LS_KEY, JSON.stringify(existingData));
  } catch (err) {
    console.error("Failed to save trial form data to localStorage", err);
  }
}

/**
 * Remove trial form data from local storage
 */
function removeTrialFormDataFromLocalStorage() {
  try {
    localStorage.removeItem(TRIAL_FORM_DATA_LS_KEY);
  } catch (err) {
    console.error("Failed to remove trial form data from localStorage", err);
  }
}

/**
 * Check if trial form data has expired
 * @param {TrialFormData} data
 */
function isTrialFormDataExpired(data) {
  if (!data?.timestamp) return true;

  const now = Date.now();
  return now - data.timestamp > TRIAL_FORM_DATA_MAX_AGE;
}

/**
 * Restore trial form data and advance to the last completed step
 * @param {TrialFormData} data
 */
function restoreAbandonedForm(data) {
  if (!data) return;

  let step1Completed = false;

  // Restore step 1 data
  if (data["step-1"]) {
    const email = selectInput("Email");
    const emailValue = data["step-1"]["Email"];
    if (emailValue) {
      email.value = emailValue;
      step1Completed = true;
    }
  }

  // Restore step 2 data
  if (step1Completed && data["step-2"]) {
    // First advance to step 2
    swapSteps("step-1", "step-2");

    const stepData = data["step-2"];
    selectInput("First-name").value = stepData["First-name"] || "";
    selectInput("Last-name").value = stepData["Last-name"] || "";
    selectInput("Company").value = stepData["Company"] || "";
    selectSelect("Country").value = stepData["Country"] || "";

    // Restore phone if it exists
    try {
      const phoneInput = selectInput("Phone");
      if (phoneInput && stepData["Phone"]) {
        phoneInput.value = stepData["Phone"];
      }
    } catch (err) {
      // skip
    }
  }
}

/**
 * @arg {string} sessEmail
 */
function setEmailValue(sessEmail) {
  const email = selectInput("Email");
  return (email.value = sessEmail);
}

const setupBackButtonListeners = () => {
  const backButtons = document.querySelectorAll(".back-button");
  for (const button of backButtons) {
    button.addEventListener("click", onBackButtonClick);
  }
};

const setupPasswordVisibilityButton = () => {
  const button = document.getElementById("password-visibility-toggle");
  button && button.addEventListener("click", togglePasswordVisibility);
};

const userInputTrackerIds = [
  "Email", // email
  "First-name", // First-name
  "Last-name", // Last-name
  "Phone", // Phone
  "Company", // Company
  "Country", // Country
  "Password", // password
  "Subdomain", // subdomain
];

function setupInputListeners() {
  for (const id of userInputTrackerIds) {
    try {
      const elm = selectById(id);
      if (elm instanceof HTMLInputElement) {
        elm?.addEventListener("keyup", onInputKeyup, { once: true });
      } else if (elm instanceof HTMLSelectElement) {
        elm?.addEventListener("change", onInputKeyup, { once: true });
      }
    } catch (err) {
      // skip
    }
  }
}

/**
 * @param {Event} evt
 */
function onInputKeyup(evt) {
  if (
    evt &&
    evt.target instanceof HTMLElement &&
    userInputTrackerIds.includes(evt.target.id)
  ) {
    sendUserInputTrack(evt.target.id);
  }
}

/**
 * @param {string} id
 */
function sendUserInputTrack(id) {
  const tracks = {};
  tracks.form_type = "Single Line Form";
  tracks.version = "2.0";
  tracks.field_name = id.toLowerCase();
  tracks.is_pd_employee = isPdEmployee(employeeSearchCriteria);
  trialFormElementUserInputDetected(tracks);
}

/**
 * @arg {HTMLFormElement} form
 * @returns {FormData}
 */
function getFormData(form) {
  return new FormData(form);
}

/** @arg {Event} evt */
const onBackButtonClick = (evt) => {
  evt.preventDefault();
  const currentStep = getCurrentStep();
  previousStep[currentStep]();
  trackBackButtonClick(currentStep);
};

/** @arg {Event} evt */
const togglePasswordVisibility = (evt) => {
  evt.preventDefault();
  const button = document.getElementById("password-visibility-toggle");
  const password = selectInput("Password");

  if (button && password instanceof HTMLInputElement) {
    const isCurrentlyVisible = password.type === "text";
    const isVisible = !isCurrentlyVisible;
    password.type = isVisible ? "text" : "password"; // Show/hide password
    button.dataset["visible"] = String(isVisible); // Update button icon
    setFocus(password);
    trackPasswordVisibilityToggled(isVisible);
  }
};

/** @arg {SubmitEvent} evt */
function onSubmit(evt) {
  evt.preventDefault();
  sessionStorage.removeItem("email"); // Remove the email in session storage in case
  // the user wants to start over with a new email
  const currentStep = getCurrentStep();
  const isValid = stepValidation[currentStep]();
  trackStep(currentStep, isValid);
  if (isValid) nextStep[currentStep]();
  else evt.stopImmediatePropagation(); // Prevent any other submit handler if there is an error
}

function getCurrentStep() {
  const formWrapperElm = selectFormWrapper();
  const step = formWrapperElm.classList;
  if (step.contains("step-1")) return "step-1";
  else if (step.contains("step-2")) return "step-2";
  else if (step.contains("step-3")) return "step-3";
  else if (step.contains("step-4")) return "step-4";
  else throw new Error("Invalid step");
}

const stepValidation = {
  "step-1": function () {
    const email = selectInput("Email");
    const isValid = checkEmailValidity(email);
    const root = selectError("step-1");
    root.classList[isValid ? "add" : "remove"]("hidden");
    if (!isValid) {
      hideIndividualErrors(root);
      showSpecificError(root, getEmailValidity(email));
    }
    return isValid;
  },
  "step-2": function () {
    // Common fields are always present in the form
    const commonFields = ["First-name", "Last-name", "Company", "Country"];
    // Optional fields may or may not be present depending on the language
    const optionalFields = ["Phone"];
    const fields = [...commonFields, ...optionalFields];
    let allValid = true;
    for (const field of fields) {
      let elm;
      try {
        elm = selectById(field);
      } catch (err) {
        if (!optionalFields.includes(field)) {
          console.log(err);
        }
        continue;
      }
      if (!(
        elm instanceof HTMLInputElement || elm instanceof HTMLSelectElement
      )) {
        allValid = false;
        continue;
      }
      allValid = allValid && elm?.checkValidity();
    }

    const root = selectError("step-2");
    root.classList[allValid ? "add" : "remove"]("hidden");
    if (!allValid) {
      hideIndividualErrors(root);
      for (const field of fields) {
        let elm;
        try {
          elm = selectById(field);
        } catch (err) {
          if (!optionalFields.includes(field)) {
            console.log(err);
          }
          continue;
        }
        if (!(
          elm instanceof HTMLInputElement || elm instanceof HTMLSelectElement
        ))
          continue;
        if (elm.validity.valueMissing || elm.validity.patternMismatch) {
          showStep2Error(root, field);
        }
      }
    }
    return allValid;
  },
  "step-3": function () {
    const password = selectInput("Password");
    const isTooShort = password.value.trim().length < 10;
    const isTooLong = 128 < password.value.trim().length;
    const isValid = password.checkValidity() && !isTooLong && !isTooShort;
    const root = selectError("step-3");
    root.classList[isValid ? "add" : "remove"]("hidden");
    if (!isValid) {
      hideIndividualErrors(root);
      const validity = {};
      /*
       * NOTE(leo): Simple object cloning doesn't work for ValidityState as it
       * doesn't expose the keys as iterable
       * const validity = { ...password.validity };
       */
      for (const key in password.validity) {
        // NOTE(leo): `for in` turns specific keys into generic strings raising a
        // typescript warning
        // @ts-expect-error
        validity[key] = password.validity[key];
      }
      validity.tooLong = isTooLong;
      validity.tooShort = isTooShort;
      showSpecificError(root, validity);
    }
    return isValid;
  },
  "step-4": function () {
    onSubdomainChange();
    onTosChange();
    return (
      selectInput("Subdomain").checkValidity() &&
      selectInput("Agree-to-tos").checkValidity() &&
      !win.turnstile.isExpired() &&
      win.turnstile.getResponse() != null
    );
  },
};

export const nextStep = {
  "step-1": function () {
    // Save step data to local storage
    const email = selectInput("Email").value.trim();
    saveStepToLocalStorage("step-1", { Email: email });
    swapSteps("step-1", "step-2");
  },
  "step-2": function () {
    // Save step data to local storage
    /** @type {Record<string, string>} */
    const stepData = {
      "First-name": selectInput("First-name").value.trim(),
      "Last-name": selectInput("Last-name").value.trim(),
      Company: selectInput("Company").value.trim(),
      Country: selectSelect("Country").value.trim(),
    };
    // Include phone if it exists (Japan only)
    try {
      const phoneInput = selectInput("Phone");
      if (phoneInput) {
        stepData["Phone"] = phoneInput.value.trim();
      }
    } catch (err) {
      // skip
    }
    saveStepToLocalStorage("step-2", stepData);
    swapSteps("step-2", "step-3");
  },
  "step-3": function () {
    swapSteps("step-3", "step-4");
    selectInput("Service-region-us").onclick = onSubdomainChange;
    selectInput("Service-region-eu").onclick = onSubdomainChange;
    selectInput("Agree-to-tos").onclick = onTosChange;
    const subdomain = selectInput("Subdomain");
    subdomain.oninput = onSubdomainChange;
    if (!subdomain.value) {
      subdomain.value = emailDomainAsSubdomainWithoutTLD(
        selectInput("Email").value,
      );
    }
    subdomain.select();
    onSubdomainChange();
    resetTurnstile(); // Not required, but a convenience for the user:
    // Reset the timer of the, up to now, hidden widget
  },
  "step-4": async function () {
    displaySubmissionActivity("submitting");

    const { status, data, request } = await submitForm();

    // If the form didn't go through reset the turnstile widget (if it is
    // visible). Otherwise if there is more than one submission with the same
    // widget params it will fail as a "duplicate".
    if (status !== "success") {
      turnstileValidityState.turnstileResponseError = false;
      turnstileValidityState.turnstileResponseError = false;
      displayTurnstileValidity();
      resetTurnstile();
    }

    if (status === "domainNotAvailable") {
      displaySubmissionActivity("not-submitting");

      const subdomain = selectInput("Subdomain");
      const unavailable = subdomain.value;
      const suggested = data;
      const subdomainErrors = selectById("subdomain-errors");
      subdomainErrors.classList.remove("hidden");
      hideIndividualErrors(subdomainErrors);
      showSpecificError(subdomainErrors, { domainTaken: true });
      selectById("chosen-domain").textContent = unavailable;
      const alternativeButton = selectById("alternative-domain");
      alternativeButton.textContent = suggested;
      alternativeButton.onclick = (evt) => {
        evt.preventDefault();
        subdomain.value = suggested;
        onSubdomainChange();
      };
      trackFailedSignup(status, suggested);
    } else if (status === "success") {
      const { accountUrl, json } = data;
      // Remove trial form data on successful signup
      removeTrialFormDataFromLocalStorage();
      // Redirect in a callback
      const afterTrack = () => window.location.assign(accountUrl);
      // Try to log the event using segment
      // Avoid using redirect in the Segment callback since this can lead
      // to a race condition where auth may fail for some users
      trackSuccessSignup(json?.account?.id);
      // Since segment might be blocked (privacy settings/ad blocker)
      // unconditionally redirect after 500 milliseconds
      setTimeout(afterTrack, 500);
    } else if (status === "turnstileError") {
      displaySubmissionActivity("not-submitting");
      turnstileValidityState.turnstileResponseError = true;
      displayTurnstileValidity();
      trackFailedSignup(status, data.join("; "));
    } else if (status === "actionableClientError") {
      swapSteps("step-4", "failure");
      win.Bugsnag?.notify(createSignupValidationError(data), (event) => {
        const errorData = {
          ...data,
          status,
          validationFields: getSignupValidationErrorFields(data),
        };
        event.addMetadata("Validation Details", errorData);
        event.request = request;
        event.severity = "warning";
      });
      trackFailedSignup(status, data.statusCode.toString());
    } /* (status === "serverError" || "clientError" || "unhandledError") */ else {
      swapSteps("step-4", "failure");
      const error =
        data instanceof Error ? data : createSignupResponseError(data);
      win.Bugsnag?.notify(error, (event) => {
        const errorData = {
          ...data,
          status,
        };
        // This metadata is used in bugsnag for a custom filter,
        // which is used to trigger PD alerts.
        // See Bugsnag saved filterset: "Account creation error (sign-up-form.js)"
        // https://app.bugsnag.com/pagerduty-1/pd-marketing-www-client/errors?filters[metaData.custom.Error%20Details][type]=empty&filters[metaData.custom.Error%20Details][value]=false&filters[event.since]=all&sort=last_seen
        event.addMetadata("Error Details", errorData);
        event.request = request;
        event.severity = "error";
      });
      const errorMessage =
        data instanceof Error ? data.message : data.statusCode.toString();
      trackFailedSignup(status, errorMessage);
    }
  },
};

const previousStep = {
  "step-1": () => {},
  "step-2": () => swapSteps("step-2", "step-1"),
  "step-3": () => swapSteps("step-3", "step-2"),
  "step-4": () => swapSteps("step-4", "step-3"),
};

/**
 * @arg {HTMLElement} root
 * @arg {string} name
 */
function showStep2Error(root, name) {
  const elm = root.querySelector(`.${name}.valueMissing`);
  if (elm instanceof HTMLElement) elm.classList.remove("hidden");
}

/**
 * @arg {Step} from
 * @arg {StepOrFailure} to
 */
function swapSteps(from, to) {
  selectStep(from).classList.add("hidden");
  const elm = selectStep(to);
  elm.classList.remove("hidden");
  const formWrapperElm = selectFormWrapper();
  formWrapperElm.classList.remove(from);
  formWrapperElm.classList.add(to);
  setFocus(elm);
}

/**
 * Runs a callback when the form becomes visible in the viewport for the first time.
 *
 * @param {() => any} callback
 */
function onFormFirstView(callback) {
  const formElm = selectForm();

  /**
   * @param {IntersectionObserverEntry[]} entries - Array of intersection observer entries
   * @param {IntersectionObserver} observer - The intersection observer instance
   */
  const handleIntersection = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.target === formElm && entry.isIntersecting) {
        callback();

        // Disconnect after first event
        observer.disconnect();
      }
    });
  };
  const observer = new IntersectionObserver(handleIntersection, {
    threshold: 0.1, // Trigger when 10% of the form is visible
  });

  observer.observe(formElm);
}

/** @arg {HTMLElement} root */
function setFocus(root) {
  const inputs =
    root instanceof HTMLInputElement
      ? [root]
      : root.getElementsByTagName("input");
  if (inputs.length > 0 && inputs[0] instanceof HTMLInputElement)
    inputs[0].focus({ preventScroll: true });
}

function onSubdomainChange() {
  const subdomain = selectInput("Subdomain");
  const subdomainErrors = selectById("subdomain-errors");
  const serviceRegionVal = selectServiceRegion().value === "eu" ? "eu." : "";
  selectById("url-container").innerHTML = `.${serviceRegionVal}pagerduty.com`;
  if (subdomain.checkValidity()) {
    subdomainErrors.classList.add("hidden");
  } else {
    subdomainErrors.classList.remove("hidden");
    hideIndividualErrors(subdomainErrors);
    showSpecificError(subdomainErrors, subdomain.validity);
  }
}

function onTosChange() {
  const tos = selectInput("Agree-to-tos");
  const tosErrorsElm = selectById("tos-errors");
  if (tos.checkValidity()) {
    tosErrorsElm.classList.add("hidden");
  } else {
    tosErrorsElm.classList.remove("hidden");
    hideIndividualErrors(tosErrorsElm);
    showSpecificError(tosErrorsElm, tos.validity);
  }
}

/** @arg {string} email */
function emailDomainAsSubdomainWithoutTLD(email) {
  return email.trim().substring(email.indexOf("@") + 1, email.indexOf("."));
}

/** @arg {"submitting" | "not-submitting"} submissionState */
function displaySubmissionActivity(submissionState) {
  const isSubmitting = submissionState === "submitting";
  const inputs = Array.from(selectStep("step-4").querySelectorAll("input"));
  for (const input of inputs) {
    if (input instanceof HTMLInputElement) input.disabled = isSubmitting;
  }
  const button = selectButton("step-4");
  button.textContent = isSubmitting
    ? button.dataset.ingestDataLabel || "Ingesting Data..."
    : button.dataset.createAccountLabel || "Create Account";
  toggleSubmitBtn(!isSubmitting);
}

/** @arg {boolean} isEnabled */
function toggleSubmitBtn(isEnabled) {
  const isDisabled = !isEnabled;
  const button = selectButton("step-4");
  button.disabled = isDisabled;
}

/**
 * @typedef {{status: "success", data: {accountUrl: string, json: any}, request: any }} FormSubOk
 * @typedef {{status: "domainNotAvailable", data: string, request: any }} FormSubNaError
 * @typedef {{status: "turnstileError", data: string[], request: any }} FormTurnstileError
 * @typedef {{status: "serverError", data: Error, request: any }} FormSubServerError
 * @typedef {{status: "clientError", data: {statusCode: number, json: any}, request: any }} FormSubClientError
 * @typedef {{status: "actionableClientError", data: {statusCode: number, json: any}, request: any }} FormSubActionableClientError
 * @typedef {{status: "unhandledError", data: Error, request: any }} FormSubUnhandledError
 * @typedef {FormSubOk | FormSubNaError | FormTurnstileError | FormSubServerError | FormSubClientError | FormSubActionableClientError | FormSubUnhandledError} FormSubmission
 */

/** @return {Promise<FormSubmission>} */
async function submitForm() {
  // Gather the data
  const subdomain = selectInput("Subdomain").value;

  const marketing_data = setVariablesFromCookies();

  const reqBody = {
    account: {
      partner: "_sign_up_",
      name: subdomain,
      subdomain,
      // A generic fallback is added due to a bug that affected some users where the timezone might be undefined or empty
      // https://support.google.com/chrome/thread/237667177
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Etc/UTC",
      account_owner: {
        email: selectInput("Email").value,
        name:
          selectInput("First-name").value +
          " " +
          selectInput("Last-name").value,
        password: selectInput("Password").value,
        company: selectInput("Company").value,
        country: selectSelect("Country").value,
        agree_tos: selectInput("Agree-to-tos").checked,
        agree_marketing: selectInput("Agree-to-marketing").checked,
      },
      marketing_data,
    },
  };

  // Phone field (Japan only)
  try {
    const phoneInput = selectInput("Phone");
    if (phoneInput) {
      reqBody.account.account_owner.phone = phoneInput.value;
    }
  } catch (err) {
    // skip
  }

  const isUS = selectServiceRegion().value === "us";

  if (win.pd.signUp == null) throw new Error("Missing window.pd.signUp");

  const data = {
    method: "POST",
    // credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Request-Id": createRequestId(),
    },
    body: JSON.stringify(reqBody),
  };

  const baseUrl = isUS ? win.pd.signUp.urlUS : win.pd.signUp.urlEU;
  if (baseUrl == null) throw new Error("Invalid window.pd.signUp.url");

  // Add Turnstile response key to query-string.
  // If the widget did not create/populate the hidden response input, route
  // through the existing Turnstile error UI instead of throwing an unhandled
  // client exception.
  const turnstileResponseInput = selectOptionalInputByName(
    "cf-turnstile-response",
  );
  if (turnstileResponseInput == null || !turnstileResponseInput.value) {
    return {
      status: "turnstileError",
      data: ["missing-turnstile-response-input"],
      request: {
        headers: data.headers,
        url: baseUrl,
        "X-Request-Id": data.headers["X-Request-Id"] ?? "NA",
      },
    };
  }

  const turnstileResp = turnstileResponseInput.value;
  const url = new URL(baseUrl, window.location.href);
  url.searchParams.append("cf-turnstile-response", turnstileResp);

  // Send it and return the response
  try {
    const response = await fetch(url, data);
    let json;
    try {
      json = await response.json();
    } catch (error) {
      // Server returned a non-JSON body (e.g. HTML error page on 500)
      throw new Error(
        `Server returned non-JSON response (status ${response.status})`,
        {
          cause: error instanceof Error ? error : new Error(String(error)),
        },
      );
    }

    // Request without password for logging purposes
    const filteredBody = { ...reqBody };
    delete filteredBody.account.account_owner.password;

    const filteredRequest = {
      headers: data.headers,
      body: filteredBody,
      url: url.toString(),
      "X-Request-Id": data.headers["X-Request-Id"] ?? "NA",
    };

    // Success
    if (response.ok) {
      // The url to the new account is contained in the header
      // https://github.com/PagerDuty/web/blob/857d7e5b982a6e4dbea51f57b4fd41245028179d/app/controllers/api/v2/accounts_controller.rb#L74
      const urlKey = "X-PagerDuty-New-Account-Entry-Uri";
      const accountUrl = response.headers.get(urlKey);

      // Validates the value before returning it
      // Empty url
      if (accountUrl == null) {
        return {
          status: "serverError",
          data: new Error(`${urlKey} is empty`),
          request: filteredRequest,
        };
      }

      // Validates the value before returning it
      // Invalid url
      try {
        new URL(accountUrl);
      } catch (err) {
        return {
          status: "serverError",
          data: new Error(`${urlKey} contains an invalid URL`),
          request: filteredRequest,
        };
      }

      // Valid url
      return {
        status: "success",
        data: { accountUrl, json },
        request: filteredRequest,
      };
    }

    // Client error
    if (response.status >= 400 && response.status < 500) {
      // A note how web returns errors:
      // Each model has an `errors` property which contains a 2d array made up
      // of a key, containing an array of one or more values. The key allows for
      // grouping similar errors around a particular model property.
      //
      // Example:
      // https://github.com/PagerDuty/web/blob/bdf8f764e3eddfe5a50d4764cca74def8e051a38/app/models/account.rb#L767
      // # Add two errors to the model
      // account.errors['subdomain'] << 'has already been taken'
      // account.errors['subdomain'] << 'could not be created, contact support'
      // user.errors['email'] << 'has already been registered'
      //
      // The return value of this api call is
      // response.error.errors['subdomain'] = ['has already been taken', 'could not be created, contact support']

      if (json?.recommendations?.subdomain) {
        return {
          status: "domainNotAvailable",
          data: json.recommendations.subdomain,
          request: filteredRequest,
        };
      } else if (response.statusText === "Turnstile Error") {
        return {
          status: "turnstileError",
          data: json?.["error-codes"],
          request: filteredRequest,
        };
        /* Sample json response body
         * HTTP/1.1 400 Turnstile Error
         * {"success":false,"error-codes":["invalid-input-response"],"messages":[]}
         */
      }
    }

    // Unrecognized response status
    if (json != null) {
      const data = { statusCode: response.status, json };
      return {
        status: isActionableSignupValidationError(data)
          ? "actionableClientError"
          : "clientError",
        data,
        request: filteredRequest,
      };
    } else throw new Error("Unrecognized response status " + response.status);
  } catch (err) {
    const data = err instanceof Error ? err : new Error(String(err));
    return { status: "unhandledError", data, request: {} };
  }
}

// /////////////////////////////////////////////////////////////////////////////
// Selectors
// /////////////////////////////////////////////////////////////////////////////

function selectForm() {
  const id = "slf-form-v2";
  const elm = document.getElementById(id);
  if (elm instanceof HTMLFormElement) return elm;
  throw new Error(`'${id}' element not found`);
}

function selectFormWrapper() {
  const id = "sign-up-form";
  const elm = document.getElementById(id);
  if (elm instanceof HTMLElement) return elm;
  throw new Error(`'${id}' element not found`);
}

/** @arg {Step} step */
function selectError(step) {
  const sel = `#${step}-fields > .validation-errors`;
  const elm = document.querySelector(sel);
  if (elm instanceof HTMLElement) return elm;
  throw new Error(`'${sel}' element not found`);
}

/** @arg {string} name */
function selectInputByName(name) {
  const sel = `[name="${name}"]`;
  const elm = document.querySelector(sel);
  if (elm instanceof HTMLInputElement) return elm;
  throw new Error(`'${sel}' element not found`);
}

/**
 * @arg {string} name
 * @returns {HTMLInputElement | null}
 */
function selectOptionalInputByName(name) {
  const elm = document.querySelector(`[name="${name}"]`);
  return elm instanceof HTMLInputElement ? elm : null;
}

/** @arg {StepOrFailure} step */
function selectButton(step) {
  const sel = `#${step}-fields .button`;
  const elm = document.querySelector(sel);
  if (elm instanceof HTMLButtonElement) return elm;
  throw new Error(`'${sel}' element not found`);
}

/** @arg {StepOrFailure} step */
function selectStep(step) {
  const id = step === "failure" ? step : `${step}-fields`;
  const elm = document.getElementById(id);
  if (elm instanceof HTMLElement) return elm;
  throw new Error(`'${id}' element not found`);
}

/** @arg {string} id */
function selectById(id) {
  const elm = document.getElementById(id);
  if (elm instanceof HTMLElement) return elm;
  throw new Error(`'${id}' element not found`);
}

function selectServiceRegion() {
  const sel = "input[name=service-region]:checked";
  const elm = document.querySelector(sel);
  if (elm instanceof HTMLInputElement) return elm;
  throw new Error(`'${sel}' element not found`);
}

/** @typedef {"step-1" | "step-2" | "step-3" | "step-4"} Step */
/** @typedef {Step | "failure"} StepOrFailure */

// /////////////////////////////////////////////////////////////////////////////
// tracking helpers
// /////////////////////////////////////////////////////////////////////////////

/**
 * @typedef {Record<string, string>} UtmMap
 *
 * Maps the query string name (as expected from
 * https://signup.pagerduty.com/api/v1/accounts)
 * to a cookie name.
 * There are two instances of this type:
 * FirstTouch and LastTouch
 */

/** @type UtmMap */
const FirstTouchUtmMap = {
  utm_campaign: "ft_utm_campaign",
  utm_medium: "ft_utm_medium",
  utm_content: "ft_utm_content",
  utm_source: "ft_utm_source",
  utm_term: "ft_utm_term",
};

/**
 * In addition to the utm paramters, gclid and mkwid are also included only for
 * the last touch. This corresponds to the implementation in "linker".
 *
 * @type UtmMap
 */
const LastTouchUtmMap = {
  utm_campaign: "lt_utm_campaign",
  utm_medium: "lt_utm_medium",
  utm_content: "lt_utm_content",
  utm_source: "lt_utm_source",
  utm_term: "lt_utm_term",
  gclid: "gclid",
  mkwid: "mkwid",
};

function setVariablesFromCookies() {
  // Use the url stored in the cookie if it isn't empty.
  // Otherwise the url of the current page.
  const ftCookie = getPDCookie("pd_first_page").trim();
  const ftPage = ftCookie ? decodeURIComponent(ftCookie) : window.location.href;
  // This page is sent with the account creation call, and is used for creating
  // utm fields in SFDC and Marketo. The issue is that we use this to populate
  // all "lt_utm_*" field values in SF/MKTO. This is misleading, since "lt_utm"s
  // might not exist in the path if the user landed on the site, clicked around,
  // and then signed up. Attribution of "lt_utm_*"s would be lost. Here we will
  // add the correct "lt_utm"s that are present in the lt_utm cookies to the url
  // that is passed.
  const ltCookie = getPDCookie("pd_last_page").trim();
  const ltPage = ltCookie ? decodeURIComponent(ltCookie) : window.location.href;
  return {
    pd_referrer: getPDCookie("pd_referrer"),
    pd_first_page: buildMarketingPageURI(ftPage, FirstTouchUtmMap),
    pd_first_visit: getPDCookie("pd_first_visit"),
    pd_last_referrer: getPDCookie("pd_last_referrer"),
    pd_last_page: buildMarketingPageURI(ltPage, LastTouchUtmMap),
    pd_last_visit: getPDCookie("pd_last_visit"),
    mkwid: mkwid(),
    gclid: gclid(),
  };
}

/**
 * @arg {string} pageUrl
 * @arg {UtmMap} utmMap
 */
function buildMarketingPageURI(pageUrl, utmMap) {
  // Read cookies as if they were query string parameters
  const utms = cookiesToUtms(utmMap);
  // Create a new URL to assign the utms as query string parameters
  let url;
  try {
    url = new URL(pageUrl);
  } catch (err) {
    url = new URL(window.location.href);
  }
  // If this page is first touch or last touch
  if (pageUrl === window.location.href) {
    // Append and override cookie values with those from the query string but
    // only if the parameter name exists in utmMap
    for (const [k, v] of url.searchParams) {
      if (k in utmMap) utms[k] = v;
    }
  }
  // Create a new query string from the merged values
  const qs = new URLSearchParams(utms);
  // Assign it and return the url as a uri encoded string
  url.search = qs.toString();
  return encodeURIComponent(url.toString());
}

/**
 * Convert cookies to an utm object by mapping the cookie names to utm names
 * @arg {UtmMap} utmMap
 */
function cookiesToUtms(utmMap) {
  /** @type {Record<string, string>} */
  const obj = {};
  for (const [utmName, cookieName] of Object.entries(utmMap)) {
    const value = getPDCookie(cookieName);
    if (value) obj[utmName] = value;
  }
  return obj;
}

////////////////////////////////////////////////////////////////////////////////

const FORM_TYPE = "Single Line Form";
const FORM_VERSION = "3";
const NA = "N/A";
const STEP_NUMBER = {
  "step-1": 1,
  "step-2": 2,
  "step-3": 3,
  "step-4": 4,
};
const STEP_NAME = {
  "step-1": "email",
  "step-2": "name",
  "step-3": "password",
  "step-4": "subdomain",
};

/**
 * @arg {Step} step
 */
function trackTurnstileNotloaded(step) {
  const step_name = STEP_NAME[step];
  var tracks = {
    form_type: FORM_TYPE,
    version: FORM_VERSION,
    step_number: STEP_NUMBER[step],
    step_name: step_name,
    step_status: "failed", // Must be failed to match legacy naming
    failure_type: "Turnstile load failure",
    error_message: "Captcha load failure",
    is_pd_employee: isPdEmployee(employeeSearchCriteria),
    mkwid: mkwid(),
    gclid: gclid(),
  };
  trialFormDependencyActivation(tracks);
}

/**
 * @arg {Step} step
 * @arg {boolean} isValid
 */
function trackStep(step, isValid) {
  const step_name = STEP_NAME[step];
  var tracks = {
    form_type: FORM_TYPE,
    version: FORM_VERSION,
    step_number: STEP_NUMBER[step],
    step_name: step_name,
    step_status: isValid ? "success" : "failed", // Must be failed to match legacy naming
    failure_type: isValid ? NA : "Validation failure",
    error_message: isValid ? NA : "failed validating " + step_name,
    is_pd_employee: isPdEmployee(employeeSearchCriteria),
    mkwid: mkwid(),
    gclid: gclid(),
  };
  trialFormNextButtonClicked(tracks);
}

/**
 * @arg {Step} step
 */
function trackBackButtonClick(step) {
  const step_name = STEP_NAME[step];
  var tracks = {
    form_type: FORM_TYPE,
    version: FORM_VERSION,
    step_number: STEP_NUMBER[step],
    step_name: step_name,
    is_pd_employee: isPdEmployee(employeeSearchCriteria),
    mkwid: mkwid(),
    gclid: gclid(),
  };
  trialFormBackButtonClicked(tracks);
}

/**
 * @arg {boolean} visibility
 */
function trackPasswordVisibilityToggled(visibility) {
  var tracks = {
    visibility: visibility,
    form_type: FORM_TYPE,
    version: FORM_VERSION,
    is_pd_employee: isPdEmployee(employeeSearchCriteria),
    mkwid: mkwid(),
    gclid: gclid(),
  };
  trialFormPasswordVisibilityToggled(tracks);
}

/**
 * @arg {string?} account_id
 * @arg {() => void } [next]
 */
function trackSuccessSignup(account_id, next) {
  try {
    const form = /** @type {HTMLFormElement} */ (
      document.getElementById("slf-form-v2")
    );
    const formData = getFormData(form) ?? new FormData();
    if (form) identifyFreeTrialShortForm(formData);

    const filterFields = [
      "email",
      "first_name",
      "last_name",
      "company",
      "job_title",
      "country",
      "subdomain",
    ];
    const cf_country = getCloudflareData()?.country;

    let filteredFormData = [...formData.entries()].reduce(
      (acc, [key, value]) => {
        if (filterFields.includes(key.toLowerCase())) acc[key] = value;
        return acc;
      },
      {
        enriched_country:
          cf_country && countryLookupTable[cf_country]
            ? countryLookupTable[cf_country]
            : NA,
      },
    );

    const tracks = {
      form_type: FORM_TYPE,
      version: FORM_VERSION,
      step_number: 4,
      step_name: "account creation",
      step_status: "success",
      failure_type: NA,
      error_message: NA,
      is_pd_employee: isPdEmployee(employeeSearchCriteria),
      account_id: account_id ?? NA,
      mkwid: mkwid(),
      gclid: gclid(),
      service_region: selectServiceRegion().value,
      agree_tos: selectInput("Agree-to-tos").value,
      form_data: filteredFormData,
    };
    trialFormAttemptedSignup(tracks, next);
  } catch (error) {
    console.error("Error tracking successful signup", error);
    win.Bugsnag?.notify(error);
  }
}

/**
 * @arg {string} failure_type
 * @arg {string} error_message
 */
function trackFailedSignup(failure_type, error_message) {
  const form = /** @type {HTMLFormElement} */ (
    document.getElementById("slf-form-v2")
  );
  if (form) identifyFreeTrialShortForm(getFormData(form));

  var tracks = {
    form_type: FORM_TYPE,
    version: FORM_VERSION,
    step_number: 4,
    step_name: "account creation",
    step_status: "failed",
    failure_type: failure_type ?? NA,
    error_message: error_message ?? NA,
    is_pd_employee: isPdEmployee(employeeSearchCriteria),
    mkwid: mkwid(),
    gclid: gclid(),
    service_region: selectServiceRegion().value,
    agree_tos: selectInput("Agree-to-tos").value,
  };
  trialFormAttemptedSignup(tracks);
}

function initCloudflareEnrichment() {
  const opts = {
    country: "n/a",
    region: "n/a",
    status: "failed",
    reason: "n/a",
  };

  let countryField;
  try {
    countryField = selectById("Country");
  } catch (err) {
    console.error("Country field not found", err);
    opts.reason = "Country field not found";
    trackCloudflareEnrichment(opts);
    return;
  }

  const cfData = getCloudflareData();
  if (cfData && cfData.country) {
    const country = /** @type {any} */ (cfData.country);
    opts.country = country;

    // @ts-ignore
    if (countryField && countryLookupTable[country]) {
      // @ts-ignore
      countryField.value = countryLookupTable[country];
    }
    opts.status = "success";
    trackCloudflareEnrichment(opts);
  } else {
    opts.reason = "Cloudflare data not available";
    trackCloudflareEnrichment(opts);
  }
}

// Trap Door SSO Experiment
const trapDoorSSOExperiment = () => {
  trapDoorSSOExperimentVariant1();
  trapDoorSSOExperimentVariant2();
};

const trapDoorSSOExperimentVariant1 = () => {
  const ls = window.localStorage;
  const ssoDiv = document.querySelector("#experiment-sso-1");
  if (!ssoDiv) return;
  const ssoButtons = ssoDiv.querySelectorAll(".slack-sso, .microsoft-sso");
  const ssoButtonDiv = ssoDiv.querySelector(".experiment-sso-buttons");
  const ssoThankYouDiv = ssoDiv.querySelector(".experiment-sso-ty");
  const ssoThankYouText = ssoThankYouDiv?.querySelector(".ty-text");

  if (!ls || !ssoButtons || !ssoButtonDiv || !ssoThankYouDiv) {
    return;
  }

  for (const button of ssoButtons) {
    button.addEventListener("click", (evt) => {
      evt.preventDefault();
      const target = evt.currentTarget;

      // Hide the SSO buttons
      ssoButtonDiv.classList.add("hidden");

      // Replace the placeholder with the SSO provider name
      const ssoProvider =
        target.dataset.ssoProvider || "a third-party provider";
      if (ssoThankYouText && ssoProvider) {
        ssoThankYouText.textContent = ssoThankYouText.textContent?.replaceAll(
          "[sso-provider]",
          ssoProvider,
        );
      }

      // Show the feedback options
      ssoThankYouDiv.classList.remove("hidden");

      // Save state to local storage
      ls.setItem("experiment-sso", "true");

      ssoDiv &&
        setTimeout(() => {
          ssoDiv.classList.add("!max-h-0"); // Hide the entire div
        }, 7000);
    });
  }
};

const trapDoorSSOExperimentVariant2 = () => {
  const ls = window.localStorage;
  const ssoDiv = document.querySelector("#experiment-sso-2");
  if (!ssoDiv) return;
  const ssoButtons = ssoDiv.querySelectorAll(".slack-sso, .microsoft-sso");
  const ssoButtonDiv = ssoDiv.querySelector(".experiment-sso-buttons");
  const ssoFeedbackDiv = ssoDiv.querySelector(".experiment-sso-feedback");
  const ssoFeedbackQuestion =
    ssoFeedbackDiv?.querySelector(".feedback-question");
  const ssoFeedbackOptions = ssoDiv.querySelectorAll(
    ".experiment-sso-feedback-option",
  );
  const ssoThankYouDiv = ssoDiv.querySelector(".experiment-sso-ty");

  if (
    !ls ||
    !ssoButtons ||
    !ssoButtonDiv ||
    !ssoFeedbackDiv ||
    !ssoFeedbackOptions ||
    !ssoThankYouDiv
  ) {
    return;
  }

  for (const button of ssoButtons) {
    button.addEventListener("click", (evt) => {
      evt.preventDefault();
      const target = evt.currentTarget;

      // Hide the buttons
      ssoButtonDiv.classList.add("hidden");

      // Replace the placeholder with the SSO provider name
      const ssoProvider =
        target.dataset.ssoProvider || "a third-party provider";
      if (ssoFeedbackQuestion && ssoProvider) {
        ssoFeedbackQuestion.innerHTML =
          ssoFeedbackQuestion.innerHTML?.replaceAll(
            "[sso-provider]",
            ssoProvider,
          );
      }

      // Shuffle the feedback options, except "Other"
      const optionsExceptOther = Array.from(ssoFeedbackOptions).filter(
        (option) => option.id !== "experiment-sso-feedback-other",
      );
      for (let i = optionsExceptOther.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        // Swap DOM elements in the parent node
        const parent = optionsExceptOther[i].parentNode;
        if (parent) {
          const referenceNode =
            j > i ? optionsExceptOther[j].nextSibling : optionsExceptOther[j];
          parent.insertBefore(optionsExceptOther[i], referenceNode);
          if (j > i) {
            parent.insertBefore(optionsExceptOther[j], optionsExceptOther[i]);
          }
        }
      }

      // Show the feedback options
      ssoFeedbackDiv.classList.remove("hidden");

      // Save state to local storage
      ls.setItem("experiment-sso", "true");
    });
  }

  for (const button of ssoFeedbackOptions) {
    button.addEventListener("click", (evt) => {
      evt.preventDefault();

      ssoFeedbackDiv.classList.add("hidden"); // Hide the feedback options
      ssoThankYouDiv.classList.remove("hidden"); // Show the thank you message

      // Hide the entire div
      ssoDiv &&
        setTimeout(() => {
          ssoDiv.classList.add("!max-h-0");
        }, 3000);
    });
  }
};
